import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Alert,
  Breadcrumb,
  Button,
  Card,
  Col,
  Empty,
  Input,
  Row,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { LinkOutlined, SearchOutlined } from '@ant-design/icons'

import * as dashboardPublicationsApi from '@/api/dashboardPublications'
import type { DashboardEmbedItem, DashboardEmbeddingSecretItem } from '@/api/dashboardPublications'

const { Title, Paragraph, Text } = Typography

export default function DashboardPublicationsPage() {
  const [workbookIdInput, setWorkbookIdInput] = useState('')
  const [entryIdInput, setEntryIdInput] = useState('')
  const [workbookId, setWorkbookId] = useState<string | null>(null)
  const [entryId, setEntryId] = useState<string | null>(null)

  const { data: secrets = [], isLoading: secretsLoading } = useQuery({
    queryKey: ['dashboard-publication-secrets', workbookId],
    queryFn: () => dashboardPublicationsApi.fetchEmbeddingSecrets(workbookId!),
    enabled: !!workbookId,
  })

  const { data: embeds = [], isLoading: embedsLoading } = useQuery({
    queryKey: ['dashboard-publication-embeds', entryId],
    queryFn: () => dashboardPublicationsApi.fetchEmbeds(entryId!),
    enabled: !!entryId,
  })

  const secretColumns: ColumnsType<DashboardEmbeddingSecretItem> = [
    {
      title: 'Secret ID',
      dataIndex: 'embeddingSecretId',
      key: 'embeddingSecretId',
      render: (value: string) => <Text code>{value}</Text>,
    },
    {
      title: 'Название',
      dataIndex: 'title',
      key: 'title',
      render: (value: string | null) => value || '—',
    },
    {
      title: 'Workbook',
      dataIndex: 'workbookId',
      key: 'workbookId',
      render: (value: string | null) => (value ? <Text code>{value}</Text> : '—'),
    },
    {
      title: 'Создан',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (value: string | null) => value || '—',
    },
  ]

  const embedColumns: ColumnsType<DashboardEmbedItem> = [
    {
      title: 'Embed ID',
      dataIndex: 'embedId',
      key: 'embedId',
      render: (value: string) => <Text code>{value}</Text>,
    },
    {
      title: 'Название',
      dataIndex: 'title',
      key: 'title',
      render: (value: string | null, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{value || '—'}</Text>
          {record.entryId ? <Text type="secondary" code>{record.entryId}</Text> : null}
        </Space>
      ),
    },
    {
      title: 'Secret',
      dataIndex: 'embeddingSecretId',
      key: 'embeddingSecretId',
      render: (value: string | null) => (value ? <Text code>{value}</Text> : '—'),
    },
    {
      title: 'Deps',
      key: 'depsIds',
      render: (_: unknown, record) =>
        record.depsIds.length > 0 ? <Tag>{record.depsIds.length}</Tag> : <Text type="secondary">0</Text>,
    },
    {
      title: 'Mode',
      key: 'publicParamsMode',
      render: (_: unknown, record) => (
        <Tag color={record.publicParamsMode ? 'orange' : 'default'}>
          {record.publicParamsMode ? 'public params' : 'signed only'}
        </Tag>
      ),
    },
  ]

  return (
    <div>
      <Breadcrumb
        items={[
          { title: 'Администрирование' },
          { title: 'Публикации дашбордов' },
        ]}
        style={{ marginBottom: 16 }}
      />

      <Space direction="vertical" size={4} style={{ marginBottom: 24 }}>
        <Space align="center">
          <Title level={4} style={{ margin: 0 }}>
            Реестр публикаций дашбордов
          </Title>
          <Tag color="processing">Read-only inventory</Tag>
        </Space>
        <Paragraph type="secondary" style={{ margin: 0, maxWidth: 960 }}>
          Этот раздел показывает существующие embedding secrets и embed-объекты из DataLens US API.
          Create / revoke / expiry UI будет следующим отдельным слоем.
        </Paragraph>
      </Space>

      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message="Граница текущего шага"
        description="Пока это только inventory над существующим embed substrate. Этот экран ничего не публикует и не меняет в runtime BI."
      />

      <Row gutter={[16, 16]}>
        <Col xs={24} xl={12}>
          <Card size="small" title="Embedding secrets по workbook">
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              <Space.Compact style={{ width: '100%' }}>
                <Input
                  value={workbookIdInput}
                  onChange={(event) => setWorkbookIdInput(event.target.value)}
                  placeholder="Введите workbook ID"
                  prefix={<SearchOutlined />}
                />
                <Button
                  type="primary"
                  icon={<LinkOutlined />}
                  onClick={() => setWorkbookId(workbookIdInput.trim() || null)}
                  disabled={!workbookIdInput.trim()}
                >
                  Показать
                </Button>
              </Space.Compact>

              {!workbookId ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Укажите workbook ID" />
              ) : (
                <Table
                  rowKey="embeddingSecretId"
                  size="small"
                  loading={secretsLoading}
                  columns={secretColumns}
                  dataSource={secrets}
                  locale={{ emptyText: 'Secrets не найдены' }}
                  pagination={false}
                />
              )}
            </Space>
          </Card>
        </Col>

        <Col xs={24} xl={12}>
          <Card size="small" title="Embeds по entry">
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              <Space.Compact style={{ width: '100%' }}>
                <Input
                  value={entryIdInput}
                  onChange={(event) => setEntryIdInput(event.target.value)}
                  placeholder="Введите entry ID"
                  prefix={<SearchOutlined />}
                />
                <Button
                  type="primary"
                  icon={<LinkOutlined />}
                  onClick={() => setEntryId(entryIdInput.trim() || null)}
                  disabled={!entryIdInput.trim()}
                >
                  Показать
                </Button>
              </Space.Compact>

              {!entryId ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Укажите entry ID" />
              ) : (
                <Table
                  rowKey="embedId"
                  size="small"
                  loading={embedsLoading}
                  columns={embedColumns}
                  dataSource={embeds}
                  locale={{ emptyText: 'Embeds не найдены' }}
                  pagination={false}
                />
              )}
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  )
}
